/**
 * P0 PRODUCTION-GRADE: HTTP 405 UX Handler 
 * ========================================
 * 
 * CONTRAT STRICT: Le backend est la source de vérité unique.
 * 
 * Priorité des règles:
 * 1. Si backend fournit is_actionable=false → ignorer silencieusement (prefetch/bot détecté)
 * 2. Si method !== 'POST' ET suspect in SILENT_SUSPECTS → ignorer (fallback frontend)
 * 3. Si is_actionable=true (ou non défini) avec POST → afficher toast erreur
 * 
 * IMPORTANT: Le frontend NE DOIT JAMAIS "deviner" ou parser le message d'erreur.
 * Toute décision UX se base exclusivement sur les champs contractuels:
 * - is_actionable (boolean)
 * - transient_hint (boolean)  
 * - error_reason (string)
 * - debug_info.suspect (string)
 */

/**
 * Classification des erreurs 405
 */
export const HTTP405_CLASSIFICATION = {
  IGNORE_SILENT: 'ignore_silent',     // Ne rien afficher (prefetch, bot, non-POST)
  SHOW_DART_FIX: 'show_dart_fix',     // Afficher fix Dart/Flutter
  SHOW_CLIENT_ERROR: 'show_client_error', // Afficher erreur client
  UNKNOWN: 'unknown'                   // Cas non géré
};

/**
 * Liste des suspects qui doivent être ignorés silencieusement.
 * Ces valeurs sont définies par le backend (middleware_post_only.py).
 */
const SILENT_SUSPECTS = [
  'safari_prefetch',
  'bot',
  'crawler_bot', 
  'link_preview',
];

/**
 * Liste des suspects qui nécessitent un affichage d'erreur (si method === POST).
 */
const ERROR_SUSPECTS = [
  'client_bug',
  'no_user_agent',
];

/**
 * Méthodes HTTP qui NE DOIVENT JAMAIS afficher de toast 405
 * SAUF si le backend dit explicitement is_actionable=true.
 */
const SILENT_METHODS = ['GET', 'HEAD', 'OPTIONS'];

/**
 * Classifie une erreur 405 selon le contrat backend.
 * 
 * LOGIQUE STRICTE (P0 POINT 1):
 * - Règle 1: Si is_actionable === false → ignorer (backend dit que c'est pas actionnable)
 * - Règle 2: Si method !== 'POST' ET suspect in SILENT_SUSPECTS → ignorer (fallback frontend)
 * - Règle 3: Sinon → afficher toast erreur (is_actionable=true ou non défini)
 * 
 * @param {Object} error - Erreur axios ou objet erreur
 * @returns {Object} { classification, shouldShowUI, debugInfo }
 */
export function classify405Error(error) {
  const httpStatus = error?.response?.status || error?.status;
  const errorData = error?.response?.data || error?.data || {};
  const debugInfo = errorData?.debug_info || {};
  const stage = errorData?.stage;
  
  // P0 CONTRAT: Signaux backend
  const isActionable = errorData?.is_actionable;  // undefined, true, or false
  const errorReason = errorData?.error_reason;
  
  // Pas une erreur 405
  if (httpStatus !== 405) {
    return {
      classification: null,
      shouldShowUI: true,
      is405: false,
      debugInfo: null
    };
  }
  
  const suspect = debugInfo?.suspect;
  const method = debugInfo?.method || 'GET';  // Par défaut GET si non spécifié
  const requestId = debugInfo?.request_id || errorData?.request_id;
  
  // ========================================================================
  // P0 POINT 1 - RÈGLE 1: Backend signal is_actionable=false → TOUJOURS ignorer
  // Ceci couvre tous les prefetch/bot détectés côté backend
  // ========================================================================
  if (isActionable === false) {
    console.info(`[UX 405] Ignoré (is_actionable=false from backend) | suspect=${suspect} | error_reason=${errorReason} | request_id=${requestId}`);
    return {
      classification: HTTP405_CLASSIFICATION.IGNORE_SILENT,
      shouldShowUI: false,
      is405: true,
      debugInfo,
      reason: 'backend_not_actionable'
    };
  }
  
  // ========================================================================
  // P0 POINT 1 - RÈGLE 2: Fallback frontend - method !== POST + suspect silencieux
  // Utilisé quand le backend n'a pas fourni is_actionable (vieux middleware)
  // ========================================================================
  if (SILENT_METHODS.includes(method?.toUpperCase()) && SILENT_SUSPECTS.includes(suspect)) {
    console.info(`[UX 405] Ignoré (method=${method} + suspect=${suspect}) | request_id=${requestId}`);
    return {
      classification: HTTP405_CLASSIFICATION.IGNORE_SILENT,
      shouldShowUI: false,
      is405: true,
      debugInfo,
      reason: 'non_post_prefetch_suspect'
    };
  }
  
  // ========================================================================
  // P0 POINT 1 - RÈGLE 3: is_actionable=true (ou non défini) → AFFICHER
  // Ceci inclut les vrais bugs client qui doivent être signalés
  // ========================================================================
  
  // 3a. Dart client → AFFICHER avec CTA Flutter
  if (suspect === 'dart_client_bug') {
    console.warn(`[UX 405] Dart client détecté | request_id=${requestId}`);
    return {
      classification: HTTP405_CLASSIFICATION.SHOW_DART_FIX,
      shouldShowUI: true,
      is405: true,
      debugInfo,
      reason: 'dart_client_bug',
      userMessage: 'Erreur client Dart/Flutter: utilisez POST au lieu de GET',
      ctaText: 'Voir la documentation Flutter',
      ctaAction: 'show_dart_fix'
    };
  }
  
  // 3b. Vrai bug client → AFFICHER toast d'erreur
  if (ERROR_SUSPECTS.includes(suspect) || isActionable === true) {
    console.error(`[UX 405] Bug client détecté | suspect=${suspect} | method=${method} | is_actionable=${isActionable} | request_id=${requestId}`, debugInfo);
    return {
      classification: HTTP405_CLASSIFICATION.SHOW_CLIENT_ERROR,
      shouldShowUI: true,
      is405: true,
      debugInfo,
      reason: suspect || 'actionable_error',
      userMessage: 'Erreur de méthode HTTP: le client a envoyé une requête incorrecte',
      technicalInfo: `Path: ${debugInfo?.path}, Method: ${method}`
    };
  }
  
  // ========================================================================
  // RÈGLE 4: Cas non géré sans is_actionable défini → ignorer par défaut (fail-safe)
  // On préfère ignorer plutôt que spammer l'utilisateur avec des faux positifs
  // ========================================================================
  
  // 4a. Si pas de debug_info → ignorer
  if (!debugInfo || Object.keys(debugInfo).length === 0) {
    console.info(`[UX 405] Pas de debug_info, ignoré (fail-safe) | request_id=${requestId}`);
    return {
      classification: HTTP405_CLASSIFICATION.IGNORE_SILENT,
      shouldShowUI: false,
      is405: true,
      debugInfo: null,
      reason: 'no_debug_info'
    };
  }
  
  // 4b. method !== POST sans suspect reconnu → probablement prefetch non classifié
  if (SILENT_METHODS.includes(method?.toUpperCase())) {
    console.info(`[UX 405] ${method} sans suspect connu, ignoré (fail-safe) | request_id=${requestId}`);
    return {
      classification: HTTP405_CLASSIFICATION.IGNORE_SILENT,
      shouldShowUI: false,
      is405: true,
      debugInfo,
      reason: `non_post_unclassified_${method?.toLowerCase()}`
    };
  }
  
  // 4c. Cas par défaut: ignorer
  console.info(`[UX 405] Suspect non reconnu (${suspect}), ignoré par défaut | request_id=${requestId}`, debugInfo);
  return {
    classification: HTTP405_CLASSIFICATION.IGNORE_SILENT,
    shouldShowUI: false,
    is405: true,
    debugInfo,
    reason: 'unclassified_fail_safe'
  };
}

/**
 * Handler principal pour les erreurs 405
 * Retourne true si l'erreur a été gérée (ne rien faire d'autre)
 * Retourne false si l'erreur doit être propagée
 * 
 * @param {Object} error - Erreur axios
 * @param {Object} options - Options de gestion
 * @param {Function} options.onDartFix - Callback pour afficher le fix Dart
 * @param {Function} options.onClientError - Callback pour afficher erreur client
 * @returns {boolean} true si géré, false si à propager
 */
export function handle405UX(error, options = {}) {
  const classification = classify405Error(error);
  
  // Pas une erreur 405
  if (!classification.is405) {
    return false; // Propager l'erreur
  }
  
  // Ignorer silencieusement
  if (!classification.shouldShowUI) {
    return true; // Géré, ne rien faire
  }
  
  // Afficher fix Dart
  if (classification.classification === HTTP405_CLASSIFICATION.SHOW_DART_FIX) {
    if (options.onDartFix) {
      options.onDartFix(classification);
    }
    return true; // Géré
  }
  
  // Afficher erreur client
  if (classification.classification === HTTP405_CLASSIFICATION.SHOW_CLIENT_ERROR) {
    if (options.onClientError) {
      options.onClientError(classification);
    }
    return true; // Géré
  }
  
  // Cas par défaut: ignorer
  return true;
}

/**
 * Hook React pour gérer les 405 dans les composants
 * 
 * Usage:
 * const { handleError } = use405Handler();
 * 
 * try {
 *   await apiCall();
 * } catch (error) {
 *   if (handleError(error)) return; // 405 géré
 *   // Gérer autres erreurs
 * }
 */
export function create405Handler(toast) {
  return function handleError(error) {
    const classification = classify405Error(error);
    
    if (!classification.is405) {
      return false;
    }
    
    if (!classification.shouldShowUI) {
      return true; // Ignoré silencieusement
    }
    
    // Afficher toast seulement pour les vraies erreurs
    if (classification.classification === HTTP405_CLASSIFICATION.SHOW_DART_FIX) {
      toast?.error?.(
        `${classification.userMessage}\n\nConsultez /api/debug/dart-violations pour le snippet correct.`,
        { duration: 10000 }
      );
      return true;
    }
    
    if (classification.classification === HTTP405_CLASSIFICATION.SHOW_CLIENT_ERROR) {
      toast?.error?.(
        `${classification.userMessage}\n\n${classification.technicalInfo}`,
        { duration: 8000 }
      );
      return true;
    }
    
    return true; // Géré par défaut
  };
}

export default {
  classify405Error,
  handle405UX,
  create405Handler,
  HTTP405_CLASSIFICATION
};
